import { supabase } from '@/integrations/supabase/client';
import { getTrail, type TrailAction } from '@/lib/actionTrail';
import { getLastError, type LastError } from '@/lib/lastError';
import { redactRoute, redactEmbedded } from '@/lib/redactRoute';

export interface BugReport {
  id: string;
  created_at: string;
  reporter_id: string | null;
  note: string;
  route: string;
  actions: TrailAction[];
  user_agent: string;
  status: 'open' | 'resolved';
  last_error: LastError | null;
  app_version: string | null;
  viewport: string | null;
  source: 'lms' | 'nest';
  guardian_id: string | null;
  reporter_name: string | null;
  guardian_name: string | null;
  children: { child_id: string; name: string; location_code: string | null }[];
  /** Chat room for this report (`get_bug_reports`) — null until
   * `ensureBugReportRoom` has been called for it (first staff reply). */
  room_id: string | null;
  /** Messages newer than the calling admin's own staff read-cursor — 0 when
   * `room_id` is null. */
  unread_count: number;
}

/** `lastError` overrides the globally captured one — auto-reports pass the
 *  error they actually caught (plus its `context`) instead of whatever
 *  uncaught error happened last. */
export async function submitBugReport({ note, lastError: caught }: { note: string; lastError?: LastError }): Promise<void> {
  const { data } = await supabase.auth.getUser();
  if (!data?.user) throw new Error('Cannot submit bug report: no authenticated user');

  const lastError = caught ?? getLastError();
  const { error } = await supabase.from('bug_reports').insert({
    note: redactEmbedded(note),
    route: redactRoute(window.location.pathname + window.location.search),
    actions: getTrail(),
    user_agent: navigator.userAgent,
    reporter_id: data.user.id,
    last_error: lastError
      ? { ...lastError, message: redactEmbedded(lastError.message), stack: lastError.stack ? redactEmbedded(lastError.stack) : undefined }
      : null,
    app_version: __BUILD_HASH__,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });
  if (error) throw error;
}

/** Rows per page for `listBugReports` — see postgrest cap audit rank 9
 *  (`docs/audits/2026-08-07-postgrest-cap-audit.md`): report volume is
 *  unbounded and append-only, so this reads deliberately paged rather than
 *  load-all. Matches `get_bug_reports`'s own default `p_limit`. */
export const BUG_REPORTS_PAGE_SIZE = 200;

export interface BugReportsPage {
  reports: BugReport[];
  /** Total matching rows across all pages (COUNT(*) OVER() from the RPC). */
  totalCount: number;
}

/** One page of the admin bug-report inbox. `page` is 0-indexed (page 0 =
 *  first `BUG_REPORTS_PAGE_SIZE` rows, newest first). */
export async function listBugReports(page = 0): Promise<BugReportsPage> {
  const { data, error } = await supabase.rpc('get_bug_reports', {
    p_limit: BUG_REPORTS_PAGE_SIZE,
    p_offset: page * BUG_REPORTS_PAGE_SIZE,
  });
  if (error) throw error;
  const rows = (data ?? []) as (BugReport & { total_count: number })[];
  return {
    reports: rows.map(({ total_count: _total_count, ...report }) => report as BugReport),
    totalCount: rows[0]?.total_count ?? 0,
  };
}

const AUTO_REPORT_PREFIX = 'Auto-report: page crash on ';
/** Written by `reportPhotoDenied` (src/modules/children/lib/reportPhotoDenied.ts). */
export const PHOTO_DENIED_REPORT_PREFIX = 'Auto-report: photo denied on ';

/** Auto-report notes are `Auto-report: page crash on <route> — <message>`
 *  (written by LazyLoadErrorBoundary, see
 *  `src/components/error-boundaries/LazyLoadErrorBoundary.tsx` `reportBug`)
 *  or `Auto-report: photo denied on <route> — <message>` (a profile photo the
 *  device could not save); the latter's message half is tagged
 *  `photo denied: ` so the two kinds never share a group.
 *  Manual notes → null. An auto-prefixed note missing the ` — ` separator is
 *  malformed — it has no route/message split to key a group on, so it also
 *  returns null (never groups) rather than falling back to the raw note,
 *  which previously fed `GroupHeaderRow`'s `'::'` split and produced garbled
 *  header cells. */
export function crashSignature(note: string): string | null {
  const isPhotoDenied = note.startsWith(PHOTO_DENIED_REPORT_PREFIX);
  if (!isPhotoDenied && !note.startsWith(AUTO_REPORT_PREFIX)) return null;
  const rest = note.slice((isPhotoDenied ? PHOTO_DENIED_REPORT_PREFIX : AUTO_REPORT_PREFIX).length);
  const sepIndex = rest.indexOf(' — ');
  if (sepIndex === -1) return null;
  const route = rest.slice(0, sepIndex).trim();
  const message = rest.slice(sepIndex + ' — '.length).trim();
  return `${route}::${isPhotoDenied ? 'photo denied: ' : ''}${message}`;
}

export async function updateBugReportStatus(
  id: string,
  status: 'open' | 'resolved'
): Promise<void> {
  const { error } = await supabase.from('bug_reports').update({ status }).eq('id', id);
  if (error) throw error;
}

/** Get-or-create the chat room for a parent-sourced bug report (idempotent —
 * `staff_bug_report_ensure_room`). Raises for LMS-sourced reports (no
 * guardian to chat with) — never call this without checking `guardian_id`
 * first. */
export async function ensureBugReportRoom(bugReportId: string): Promise<string> {
  const { data, error } = await supabase.rpc('staff_bug_report_ensure_room', {
    p_bug_report_id: bugReportId,
  });
  if (error) throw error;
  return data as string;
}
